import type { Profile } from './profile';
import { createRng, seedFromString, shuffle } from './rng';
import type { Balance, ChallengeTemplate, MapDef } from './types';

/** ISO week of a UTC instant as "YYYY-Www": challenges rotate on Monday 00:00 UTC. */
export function weekKey(now: number = Date.now()): string {
  const d = new Date(now);
  const day = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = day.getUTCDay() || 7;
  day.setUTCDate(day.getUTCDate() + 4 - dow);
  const yearStart = Date.UTC(day.getUTCFullYear(), 0, 1);
  const week = Math.ceil(((day.getTime() - yearStart) / 86_400_000 + 1) / 7);
  return `${day.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

export const CHALLENGE_SEED_RE = /^(\d{4}-W\d{2}):([a-z0-9_-]{1,32})$/;

export function challengeSeedLabel(week: string, id: string): string {
  return `${week}:${id}`;
}

/**
 * This week's challenges, the same for everyone: a seeded shuffle of the pool, taking at most one
 * per map so the week always spans several maps, then filling up if the pool is short on maps.
 */
export function weeklyChallenges(week: string, balance: Balance): ChallengeTemplate[] {
  const rng = createRng(seedFromString(`challenges:${week}`));
  const order = shuffle(rng, balance.challenges.pool.map((_, i) => i));
  const picked: ChallengeTemplate[] = [];
  const mapsUsed = new Set<string>();
  for (const idx of order) {
    if (picked.length >= balance.challenges.perWeek) break;
    const c = balance.challenges.pool[idx]!;
    if (mapsUsed.has(c.mapId)) continue;
    mapsUsed.add(c.mapId);
    picked.push(c);
  }
  for (const idx of order) {
    if (picked.length >= balance.challenges.perWeek) break;
    const c = balance.challenges.pool[idx]!;
    if (!picked.includes(c)) picked.push(c);
  }
  // Easiest map first, so the list reads as a ladder.
  return picked.sort((a, b) => a.mapId.localeCompare(b.mapId));
}

export function challengeById(week: string, id: string, balance: Balance): ChallengeTemplate | null {
  return weeklyChallenges(week, balance).find((c) => c.id === id) ?? null;
}

/** Waves that must be cleared to complete the challenge on its map. */
export function challengeGoal(c: ChallengeTemplate, map: MapDef): number {
  return c.goalWaves ?? map.waves.length;
}

/** Marks a challenge done for the week and pays its medals once. Returns false when it was already done. */
export function completeChallenge(p: Profile, week: string, c: ChallengeTemplate): boolean {
  if (p.challenges.week !== week) p.challenges = { week, done: [] };
  if (p.challenges.done.includes(c.id)) return false;
  p.challenges.done.push(c.id);
  p.medals = (p.medals ?? 0) + c.medals;
  return true;
}

export function challengeDone(p: Profile, week: string, id: string): boolean {
  return p.challenges.week === week && p.challenges.done.includes(id);
}
